import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Ordering } from '@idk-web/core-utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons/faArrowDown';
import { Styling } from '@/styling';
import { Overlay } from '@/components/visual/Overlay';
import { SpinningCircleLoader } from '@/components/visual/Loader';
import { Box } from '@/components/layout/Box';
import { BareCheckbox, FilledCheckbox } from '@/components/input/Checkbox';
import { StandardCard } from '@/components/card/standard';

const Container = styled(StandardCard)`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  border-radius: 8px;
  border-collapse: collapse;
  overflow: auto;
  scrollbar-color: ${({ theme }) => theme.palette.primary.main} white;

  &::-webkit-scrollbar {
    width: 5px;
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.primary.main};
  }
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
`;

const Header = styled.thead`
  ${Styling.typography('body')};
  color: ${({ theme }) => theme.palette.grayscale.white};
`;

const Body = styled.tbody`
  ${Styling.typography('body')};
`;

const Row = styled.tr`
  color: ${({ theme }) => theme.palette.primary.text};

  &:hover {
    background-color: #fbfbfb; // TODO move to theme
  }
`;

type CellProps = {
  align?: 'left' | 'center' | 'right';
};

const HeaderCell = styled.th<CellProps>`
  background-color: ${({ theme }) => theme.palette.primary.main};
  padding: ${Styling.spacing(3, 1.5)};
  position: sticky;
  top: 0;
  text-align: ${({ align }) => align ?? 'left'};
  white-space: nowrap;
`;

const DataCell = styled.td`
  padding: ${Styling.spacing(3, 1.5)};
  border-bottom: 1px solid #edf2f7; // TODO move to theme
`;

const ColumnSelectCheckbox = styled(BareCheckbox)`
  vertical-align: middle;

  & * {
    border: none !important;
  }
`;

const RowSelectCheckbox = styled(FilledCheckbox)`
  vertical-align: middle;
`;

const SortableField = styled(Box).attrs(({ theme }) => ({
  alignY: 'center',
  spacing: theme.spacing[1],
}))`
  width: fit-content;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.light4};
  }
`;

const EmptyContent = styled(Box).attrs({
  alignX: 'center',
  alignY: 'center',
})`
  ${Styling.typography('body')}
  padding: ${Styling.spacing(6, 3)};
  color: ${({ theme }) => theme.palette.primary.text};
`;

type LoadingOverlayProps = {
  show: boolean;
  offset: number;
};

const LoadingOverlay: FC<LoadingOverlayProps> = ({ show, offset }) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `${offset}px`,
        right: `-${offset}px`,
        pointerEvents: 'none',
      }}
    >
      <Overlay show={show} color="rgba(255, 255, 255, 0.5)">
        <Box
          alignX="center"
          alignY="center"
          style={{ position: 'absolute', width: '100%', height: '100%' }}
        >
          <SpinningCircleLoader />
        </Box>
      </Overlay>
    </div>
  );
};

export type TableColumn<T> = {
  key: string;
  header: ReactNode;
  accessor(row: T): ReactNode;
  sortable?: boolean;
  direction?: Ordering;
  headerAlignment?: 'left' | 'center' | 'right';
  headerWidth?: string;
};

export type DataTableProps<T extends object> = {
  loading?: boolean;
  multiSelect?: boolean;
  selections?: T[];
  onSelect?(selection: T[]): void;
  onSort?(column: string): void;
  columns: TableColumn<T>[];
  rows: T[];
  emptyText?: ReactNode;
};

export const DataTable = <T extends object>({
  loading = false,
  multiSelect = false,
  selections,
  columns,
  rows,
  emptyText,
  onSort,
  onSelect,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        setScrollLeft(ref.current?.scrollLeft ?? 0);
      }
    };

    ref.current?.addEventListener('scroll', handleScroll);

    return () => ref.current?.removeEventListener('scroll', handleScroll);
  }, []);

  if (emptyText === undefined) {
    emptyText = t('input.no_data');
  }

  return (
    <Container ref={ref}>
      <LoadingOverlay show={loading} offset={scrollLeft} />
      <Table>
        <Header>
          <tr>
            {columns.map((column) => (
              <HeaderCell
                key={column.key}
                align={column.headerAlignment}
                style={{ width: column.headerWidth }}
              >
                {!column.sortable && column.header}
                {column.sortable && (
                  <SortableField onClick={() => onSort?.(column.key)}>
                    {column.header}
                    {column.direction === Ordering.ASCENDING && (
                      <FontAwesomeIcon icon={faArrowDown} size="sm" />
                    )}
                    {column.direction === Ordering.DESCENDING && (
                      <FontAwesomeIcon icon={faArrowUp} size="sm" />
                    )}
                  </SortableField>
                )}
              </HeaderCell>
            ))}
            {selections && (
              <HeaderCell>
                {multiSelect && rows.length > 0 && (
                  <ColumnSelectCheckbox
                    icon={
                      selections.length ===
                      rows.length ? undefined /* default */ : (
                        <FontAwesomeIcon icon={faMinus} size="lg" />
                      )
                    }
                    checked={selections.length > 0}
                    onChange={() =>
                      onSelect?.(
                        selections.length === rows.length
                          ? selections
                          : [...rows, ...selections],
                      )
                    }
                  />
                )}
              </HeaderCell>
            )}
          </tr>
        </Header>
        <Body>
          {rows.map((row, index) => (
            <Row key={String(index)}>
              {columns.map((column) => (
                <DataCell key={column.key}>{column.accessor(row)}</DataCell>
              ))}
              {selections && (
                <DataCell>
                  <RowSelectCheckbox
                    checked={selections.includes(row)}
                    onChange={() => onSelect?.([row])}
                  />
                </DataCell>
              )}
            </Row>
          ))}
        </Body>
      </Table>
      {rows.length === 0 && (
        <EmptyContent>{!loading && emptyText}</EmptyContent>
      )}
    </Container>
  );
};
